import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  useServices,
  useCreateService,
  useUpdateService,
  useDeleteService,
} from '../../hooks/useServices';
import { useDepartments } from '../../hooks/useDepartments';
import type { Service, ServiceCategory, ServiceUnit } from '../../lib/servicesApi';
import { Select } from '../ui/Select';
import { useConfirm } from '../ui/ConfirmProvider';

// Settings → "Servicii" (services nomenclature).
//
// Two-pane layout mirroring DepartmentsSettings: service list + create on the
// left, editor on the right. Services are attachable to projects (many), tasks
// (one), people (many) and money (invoices / SmartBill / bank lines), and an
// optional department drives the per-department financial analytics.
// Deleting a service detaches its tags — prefer deactivating to keep history.

const COLOR_SWATCHES = [
  '#3b82f6',
  '#eab308',
  '#22c55e',
  '#16a34a',
  '#a855f7',
  '#f97316',
  '#ef4444',
  '#64748b',
];

const DEFAULT_COLOR = '#3b82f6';

export const SERVICE_CATEGORY_LABELS: Record<ServiceCategory, string> = {
  DEVELOPMENT: 'Dezvoltare',
  DESIGN: 'Design',
  PM: 'Project Management',
  QA: 'Testare (QA)',
  CONSULTING: 'Consultanță',
  MAINTENANCE: 'Mentenanță',
  OTHER: 'Altele',
};

const SERVICE_UNIT_LABELS: Record<ServiceUnit, string> = {
  HOUR: 'oră',
  PROJECT: 'proiect',
  MONTH: 'lună',
  PIECE: 'bucată',
};

export function ServicesSettings() {
  const { data: services, isLoading, isError, error } = useServices(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = services?.find((s) => s.id === selectedId) ?? services?.[0] ?? null;

  return (
    <div className="max-w-4xl">
      <p className="text-sm text-gray-500">
        Configurați nomenclatorul de servicii al companiei. Serviciile se pot atașa la proiecte,
        sarcini, colegi și la bani (facturi, reconciliere bancară) și alimentează analiza financiară
        pe servicii și departamente.
      </p>

      {isLoading && <p className="mt-6 text-sm text-gray-500">Se încarcă serviciile...</p>}
      {isError && (
        <p className="mt-6 text-sm text-red-600">
          Serviciile nu au putut fi încărcate
          {error instanceof Error ? `: ${error.message}` : ''}.
        </p>
      )}

      {services && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[280px_1fr]">
          <ServiceList
            services={services}
            selectedId={selected?.id ?? null}
            onSelect={setSelectedId}
          />
          {selected ? (
            <ServiceEditor key={selected.id} service={selected} />
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
              Niciun serviciu. Creați unul pentru a începe.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Service list + create ----------------------------------------------------

function ServiceList({
  services,
  selectedId,
  onSelect,
}: {
  services: Service[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const createService = useCreateService();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    try {
      const created = await createService.mutateAsync({ name: trimmed, color: DEFAULT_COLOR });
      setName('');
      setCreating(false);
      onSelect(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Serviciul nu a putut fi creat.');
    }
  }

  return (
    <div>
      <div className="flex flex-col gap-1">
        {services.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onSelect(s.id)}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${
              s.id === selectedId
                ? 'bg-brand-50 font-medium text-brand-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: s.color ?? '#9ca3af' }}
            />
            <span className={`flex-1 truncate ${s.isActive ? '' : 'text-gray-400 line-through'}`}>
              {s.name}
            </span>
            {s.department && (
              <span className="max-w-[90px] shrink-0 truncate text-xs text-gray-400">
                {s.department.name}
              </span>
            )}
          </button>
        ))}
      </div>

      {creating ? (
        <div className="mt-3 rounded-md border border-gray-200 p-3">
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate();
            }}
            placeholder="Nume serviciu"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={!name.trim() || createService.isPending}
              className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createService.isPending ? 'Se creează...' : 'Creează'}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setError(null);
                setName('');
              }}
              className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
        >
          <Plus size={15} /> Serviciu nou
        </button>
      )}
    </div>
  );
}

// --- Service editor -------------------------------------------------------------

function ServiceEditor({ service }: { service: Service }) {
  const updateService = useUpdateService();
  const deleteService = useDeleteService();
  const { data: departments } = useDepartments();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(service.name);
  const [description, setDescription] = useState(service.description ?? '');
  const [departmentId, setDepartmentId] = useState<string>(service.departmentId ?? '');
  const [category, setCategory] = useState<ServiceCategory>(service.category);
  const [unit, setUnit] = useState<ServiceUnit>(service.unit);
  const [price, setPrice] = useState<string>(service.price === null ? '' : String(service.price));
  const [color, setColor] = useState(service.color ?? DEFAULT_COLOR);
  const [isActive, setIsActive] = useState(service.isActive);

  const parsedPrice = price.trim() === '' ? null : Number(price.replace(',', '.'));
  const priceInvalid = parsedPrice !== null && (!Number.isFinite(parsedPrice) || parsedPrice < 0);

  const dirty =
    name.trim().length > 0 &&
    !priceInvalid &&
    (name.trim() !== service.name ||
      description.trim() !== (service.description ?? '') ||
      departmentId !== (service.departmentId ?? '') ||
      category !== service.category ||
      unit !== service.unit ||
      parsedPrice !== service.price ||
      color.toLowerCase() !== (service.color ?? DEFAULT_COLOR).toLowerCase() ||
      isActive !== service.isActive);

  async function handleSave() {
    setError(null);
    try {
      await updateService.mutateAsync({
        id: service.id,
        input: {
          name: name.trim(),
          description: description.trim() === '' ? null : description.trim(),
          departmentId: departmentId === '' ? null : departmentId,
          category,
          unit,
          price: parsedPrice,
          color,
          isActive,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Serviciul nu a putut fi salvat.');
    }
  }

  async function handleDelete() {
    setError(null);
    const usage =
      service.taskCount + service.projectCount + service.personCount + service.invoiceCount;
    const details =
      usage > 0
        ? ` ${service.taskCount} sarcini, ${service.projectCount} proiecte, ${service.personCount} colegi și ${service.invoiceCount} facturi vor rămâne fără acest serviciu. Recomandat: dezactivați serviciul în loc să-l ștergeți.`
        : '';
    if (
      !(await confirm({
        message: `Ștergeți serviciul "${service.name}"?${details} Această acțiune nu poate fi anulată.`,
        danger: true,
      }))
    )
      return;
    try {
      await deleteService.mutateAsync(service.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Serviciul nu a putut fi șters.');
    }
  }

  const departmentOptions = [
    { value: '', label: 'Fără departament' },
    ...(departments ?? []).map((d) => ({ value: d.id, label: d.name })),
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-col gap-3">
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Nume serviciu
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Descriere
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Departament"
            value={departmentId}
            onChange={(v) => setDepartmentId(v)}
            options={departmentOptions}
            size="sm"
          />
          <Select
            label="Categorie"
            value={category}
            onChange={(v) => setCategory(v as ServiceCategory)}
            options={Object.entries(SERVICE_CATEGORY_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
            size="sm"
          />
          <Select
            label="Unitate"
            value={unit}
            onChange={(v) => setUnit(v as ServiceUnit)}
            options={Object.entries(SERVICE_UNIT_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
            size="sm"
          />
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Preț standard (RON)
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="—"
              className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm ${
                priceInvalid ? 'border-red-400' : 'border-gray-300'
              }`}
            />
          </label>
        </div>

        <div>
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Culoare</span>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-8 w-9 cursor-pointer rounded border border-gray-300"
              aria-label="Culoare serviciu"
            />
            <div className="flex flex-wrap gap-1">
              {COLOR_SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  style={{ backgroundColor: c }}
                  className={`h-5 w-5 rounded-full ${
                    color.toLowerCase() === c.toLowerCase()
                      ? 'ring-2 ring-gray-400 ring-offset-1'
                      : ''
                  }`}
                  aria-label={`Folosește culoarea ${c}`}
                />
              ))}
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Serviciu activ</span>
        </label>

        <p className="text-xs text-gray-400">
          Utilizări: {service.taskCount} sarcini · {service.projectCount} proiecte ·{' '}
          {service.personCount} colegi · {service.invoiceCount} facturi
        </p>

        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || updateService.isPending}
            className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Salvează modificările
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteService.isPending}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Trash2 size={14} /> Șterge serviciu
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
